import { useCallback } from 'react';
import { LineItem } from 'shared/types/wishlist/LineItem';
import { Wishlist } from 'shared/types/wishlist/Wishlist';
import useSWR, { mutate, SWRResponse } from 'swr';
import { sdk } from 'sdk';
import { revalidateOptions } from 'frontastic';

const getTime = (value?: string | Date) => {
  if (!value) return 0;

  if (value instanceof Date) {
    return value.getTime();
  }

  return new Date(value).getTime();
};

const sortItemsByRecent = (items: LineItem[]) => [...items].sort((a, b) => getTime(b.addedAt) - getTime(a.addedAt));
const WISHLIST_KEY = '/action/wishlist/getWishlist';
const useWishlist = () => {
  const extensions = sdk.composableCommerce;

  const result = useSWR(WISHLIST_KEY, () => extensions.wishlist.getWishlist(), revalidateOptions);

  const data = (result.data?.isError ? {} : { data: result.data?.data }) as SWRResponse<Wishlist>;

  const totalWishlistItems = data.data?.lineItems?.reduce((acc, curr) => acc + (curr.count as number), 0) ?? 0;

  const updateWishlist = async ({
    newWishlist,
    actionPromise,
  }: {
    newWishlist: Wishlist;
    actionPromise: Promise<any>;
  }) => {
    return mutate(
      WISHLIST_KEY,
      async () => {
        const res = await actionPromise;

        if (res.isError) {
          return { data: newWishlist };
        }

        return {
          data: {
            ...res.data,
            lineItems: sortItemsByRecent(res.data.lineItems ?? []),
          },
        };
      },
      {
        optimisticData: { data: newWishlist },
        rollbackOnError: true,
      },
    );
  };

  const addToWishlist = useCallback(async (wishlist: Wishlist, lineItem: LineItem, count = 1) => {
    if (!wishlist) return;

    const sku = lineItem?.variant?.sku;
    if (!sku) return;

    const extensions = sdk.composableCommerce;

    const optimisticItem = {
      ...lineItem,
      addedAt: new Date(),
    };

    const newWishlist = {
      ...wishlist,
      lineItems: sortItemsByRecent([...(wishlist.lineItems ?? []), optimisticItem]),
    };

    const hasProjectId = Boolean(lineItem?.custom?.fields?.mediaClipProjectId);

    const payload = {
      variant: { sku },
      count: hasProjectId ? lineItem.count : count,
      ...(hasProjectId ? { custom: lineItem.custom } : {}),
    };

    const actionPromise = extensions.wishlist.addItem(payload);

    await updateWishlist({
      newWishlist,
      actionPromise,
    });

    return actionPromise;
  }, []);

  const removeLineItem = useCallback(async (wishlist: Wishlist, lineItem: Partial<LineItem>) => {
    if (!wishlist) return;

    const extensions = sdk.composableCommerce;

    const newWishlist = {
      ...wishlist,
      lineItems: sortItemsByRecent(wishlist.lineItems?.filter((item) => item.lineItemId !== lineItem.lineItemId) ?? []),
    };

    const actionPromise = extensions.wishlist.removeItem({
      lineItem: { id: lineItem.lineItemId as string },
    });

    await updateWishlist({
      newWishlist,
      actionPromise,
    });

    return actionPromise;
  }, []);

  const deleteWishlist = useCallback(async (wishlist: Wishlist) => {
    const res = await sdk.callAction({ actionName: 'wishlist/deleteWishlist' });
    const newWishlist = {
      ...wishlist,
      lineItems: [],
    };

    if (!res.isError) {
      await mutate(WISHLIST_KEY, res.data, {
        optimisticData: { data: newWishlist },
        rollbackOnError: true,
      });
    }
  }, []);

  const updateLineItem = useCallback(async (wishlist: Wishlist, lineItem: LineItem, count = 1) => {
    const extensions = sdk.composableCommerce;

    const newWishlist = {
      ...wishlist,
      lineItems:
        wishlist.lineItems?.map((item) => {
          if (item.lineItemId === lineItem.lineItemId) {
            return { ...lineItem, count: ++count };
          }
        }) ?? [],
    };

    const res = await extensions.wishlist.updateItem({
      lineItem: { id: lineItem.lineItemId },
      custom: lineItem?.custom,
      count,
    });

    if (!res.isError) {
      await mutate(WISHLIST_KEY, res.data, {
        optimisticData: newWishlist,
        rollbackOnError: true,
      });
    }
  }, []);

  return {
    ...data,
    totalItems: totalWishlistItems,
    addToWishlist,
    removeLineItem,
    deleteWishlist,
    updateLineItem,
  };
};

export default useWishlist;
